// Optional: parse the receipt with an on-device LLM when the browser ships one.
//
// Chrome (and Chromium-based browsers) expose the built-in Prompt API — either
// as a global `LanguageModel` or the older `window.ai.languageModel`. Everything
// runs locally; no text leaves the machine. Firefox has no web-exposed LLM API
// today, so there we simply report "unavailable" and the heuristic parser
// (parser.ts) does the job. This module is fully feature-detected and every
// path degrades gracefully.

import type { Item, ParseResult } from "../models/types.js";

interface LMSession {
  prompt(input: string): Promise<string>;
  destroy?(): void;
}
interface LMFactory {
  availability?(): Promise<string>;
  capabilities?(): Promise<{ available: string }>;
  create(opts?: unknown): Promise<LMSession>;
}

function findFactory(): LMFactory | null {
  const scope = globalThis as unknown as {
    LanguageModel?: LMFactory;
    ai?: { languageModel?: LMFactory };
  };
  return scope.LanguageModel ?? scope.ai?.languageModel ?? null;
}

/** Is an on-device language model usable right now? Handles both the new and old
 *  shapes of the API, and treats "downloadable" as usable (create() triggers it). */
export async function llmAvailable(): Promise<boolean> {
  const lm = findFactory();
  if (!lm) return false;
  try {
    if (lm.availability) {
      const availability = await lm.availability();
      return ["available", "downloadable", "downloading"].includes(availability);
    }
    if (lm.capabilities) {
      const caps = await lm.capabilities();
      return caps.available === "readily" || caps.available === "after-download";
    }
  } catch {
    return false;
  }
  return false;
}

const SYSTEM = `You extract purchasable line items from noisy receipt OCR text.
Return ONLY a JSON object, no prose, no code fences, shaped exactly:
{"currency":"€","total":<number or null>,"items":[{"name":<string>,\
"qty":<number or null>,"unitPrice":<number or null>,"lineTotal":<number or null>}]}
Rules:
- Use a dot as the decimal separator in all numbers (12.50, not 12,50).
- Exclude subtotal, tax/VAT, tip/service and grand-total lines from "items"; \
put the printed grand total in "total".
- If a quantity is shown, set qty; otherwise null.
- Keep item names as printed (any language). Do not invent items.`;

interface RawItem {
  name?: unknown;
  qty?: unknown;
  unitPrice?: unknown;
  lineTotal?: unknown;
}
interface RawResult {
  currency?: unknown;
  total?: unknown;
  items?: RawItem[];
}

function parseNum(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function extractJson(text: string): RawResult | null {
  const cleaned = text.replace(/```json/gi, "").replace(/```/g, "").trim();
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) return null;
  try {
    return JSON.parse(cleaned.slice(start, end + 1)) as RawResult;
  } catch {
    return null;
  }
}

let counter = 0;
function makeId(): string {
  return `llm-${Date.now().toString(36)}-${(counter++).toString(36)}`;
}

/** Try to parse with the on-device model. Throws if unavailable or if the model
 *  returns something unusable — callers fall back to the heuristic parser. */
export async function parseWithLlm(ocrText: string): Promise<ParseResult> {
  const lm = findFactory();
  if (!lm) throw new Error("No on-device model available.");

  const systemOpts = { initialPrompts: [{ role: "system", content: SYSTEM }] };
  let session: LMSession | null = null;
  try {
    session = await lm.create(systemOpts as unknown);
  } catch {
    // Older API has no system role support: fold instructions into the prompt.
    session = await lm.create();
  }

  const reply = await session.prompt(
    `${SYSTEM}\n\nReceipt OCR text:\n"""\n${ocrText}\n"""`,
  );
  session.destroy?.();

  const raw = extractJson(reply);
  if (!raw || !Array.isArray(raw.items)) {
    throw new Error("Model did not return usable JSON.");
  }

  const items: Item[] = raw.items
    .map((rawItem): Item | null => {
      const name = typeof rawItem.name === "string" ? rawItem.name.trim() : "";
      const qty = parseNum(rawItem.qty);
      const unitPrice = parseNum(rawItem.unitPrice);
      let lineTotal = parseNum(rawItem.lineTotal);
      if (lineTotal == null && unitPrice != null) lineTotal = (qty ?? 1) * unitPrice;
      if (!name && lineTotal == null) return null;
      return { id: makeId(), name, qty, unitPrice, lineTotal, selected: false, source: "" };
    })
    .filter((candidate): candidate is Item => candidate !== null);

  if (!items.length) throw new Error("Model found no items.");

  return {
    items,
    detectedTotal: parseNum(raw.total),
    currency: typeof raw.currency === "string" && raw.currency ? raw.currency : "€",
    method: "llm",
  };
}
